import { StrictMode, useMemo } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter, Navigate, useLocation } from 'react-router-dom';
import { QueryCache, QueryClient, QueryClientProvider, useQuery } from '@tanstack/react-query';
import './index.css';
import { api, ApiError, setTenantSlug } from './lib/api';
import { ToastProvider } from './ui/feedback';
import { GarlicMark, Spinner } from './ui/primitives';
import { ApexApp } from './apex/ApexApp';
import { TenantApp, type TenantContext } from './tenant/TenantApp';
import { rememberRestaurant } from './apex/PanelEntry';

function makeQueryClient(): QueryClient {
  const qc: QueryClient = new QueryClient({
    // Sessão expirada ou revogada no meio do uso: volta para a tela de login correspondente.
    queryCache: new QueryCache({
      onError: (err, query) => {
        if (!(err instanceof ApiError) || err.status !== 401) return;
        const key = query.queryKey[0];
        if (typeof key === 'string' && key.startsWith('dev-') && key !== 'dev-session') void qc.invalidateQueries({ queryKey: ['dev-session'] });
        if (typeof key === 'string' && key.startsWith('staff-') && key !== 'staff-me') void qc.invalidateQueries({ queryKey: ['staff-me'] });
      },
    }),
    defaultOptions: {
      queries: {
        retry: (count, err) => !(err instanceof ApiError && err.status < 500) && count < 2,
        refetchOnWindowFocus: false,
      },
    },
  });
  return qc;
}

const apexClient = makeQueryClient();

/** /r/{slug}/... (clientes) e /painel/{slug}/... (equipe) são telas de restaurante; o resto é do Garlic. */
const TENANT_PATH = /^\/(r|painel)\/([^/]+)/;

function Root() {
  const { pathname } = useLocation();
  const m = TENANT_PATH.exec(pathname);
  if (!m) {
    setTenantSlug(null);
    return (
      <QueryClientProvider client={apexClient}>
        <ApexApp />
      </QueryClientProvider>
    );
  }
  const slug = decodeURIComponent(m[2]!).toLowerCase();
  return <TenantRoot key={slug} slug={slug} area={m[1] as 'r' | 'painel'} />;
}

/**
 * Cada restaurante tem o próprio cache de consultas: trocar de /painel/a para /painel/b nunca mostra,
 * nem por um instante, dados em cache do outro restaurante.
 */
function TenantRoot({ slug, area }: { slug: string; area: 'r' | 'painel' }) {
  setTenantSlug(slug); // antes de qualquer consulta dos filhos
  const client = useMemo(() => makeQueryClient(), []);
  return (
    <QueryClientProvider client={client}>
      <TenantGate slug={slug} area={area} />
    </QueryClientProvider>
  );
}

function TenantGate({ slug, area }: { slug: string; area: 'r' | 'painel' }) {
  const location = useLocation();
  const ctx = useQuery({ queryKey: ['context'], queryFn: () => api<TenantContext>('/api/context'), staleTime: 60_000 });
  if (ctx.isPending) return <Spinner />;
  if (ctx.isError) {
    const notFound = ctx.error instanceof ApiError && ctx.error.status === 404;
    return (
      <main className="grid min-h-dvh place-items-center px-4 text-center">
        <div>
          <GarlicMark className="mx-auto size-12" />
          <h1 className="mt-4 font-display text-2xl font-semibold">{notFound ? 'Restaurante não encontrado' : 'Não foi possível carregar'}</h1>
          <p className="mt-2 text-ink-3">
            {notFound ? 'Confira o endereço digitado ou o QR Code da mesa.' : 'Verifique sua conexão e tente novamente.'}
          </p>
          {notFound && area === 'painel' && <a href="/painel" className="mt-6 inline-block text-sm underline">Procurar meu restaurante</a>}
        </div>
      </main>
    );
  }
  const current = ctx.data.restaurant.slug;
  if (current !== slug) {
    // Endereço antigo (restaurante mudou de slug): a API redirecionou; leva a tela para o endereço atual.
    const rest = location.pathname.replace(TENANT_PATH, '');
    return <Navigate to={`/${area}/${current}${rest}${location.search}`} replace />;
  }
  if (area === 'painel') rememberRestaurant(current, ctx.data.restaurant.name);
  return <TenantApp ctx={ctx.data} />;
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <ToastProvider>
      <BrowserRouter>
        <Root />
      </BrowserRouter>
    </ToastProvider>
  </StrictMode>,
);
